<template>
    <Loading v-if="loading"/>
    <Page>
        <Container>
            <div class="mx-auto max-w-screen-md">

                <div class="flex items-center justify-between mb-6">
                    <Title>{{pageTitle}}</Title>
                </div>

                <FormSections>

                    <FormSection title="Automate" description="Settings to streamline your workouts.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Last Band(s)</Label>
                                <Help>
                                    Automatically select the last band(s) used when an exercise is added.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.autoLastBands"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Open Timer</Label>
                                <Help>
                                    Automatically open the timer when editing an exercise.
                                </Help>
                                <Help class="text-red-500" v-if="form.fields.autoOpenTimer && !form.fields.exerciseTimer">
                                    This setting won't do anything, since the Exercise Timer is disabled.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.autoOpenTimer"/>
                        </Field>

                    </FormSection>

                    <FormSection title="Miscellaneous" description="A few random settings to help.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Zero Rep Warning</Label>
                                <Help>
                                    This will warn you before letting you save a workout with zero reps.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.warnZeroReps"/>
                        </Field>

                    </FormSection>

                    <FormSection title="Workout Features"
                                 description="Settings to show/hide features on the Workout form.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Timer</Label>
                                <Help>
                                    Enable/Disable the timer related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.workoutTimer"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Progress Photo</Label>
                                <Help>
                                    Enable/Disable the progress photo related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.workoutProgressPhoto"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Notes</Label>
                                <Help>
                                    Enable/Disable the notes related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.workoutNotes"/>
                        </Field>

                    </FormSection>

                    <FormSection title="Exercise Features"
                                 description="Settings to show/hide features on the Exercise form.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Timer</Label>
                                <Help>
                                    Enable/Disable the timer related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.exerciseTimer"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Advanced Toggle</Label>
                                <Help>
                                    Enable/Disable the advanced toggle input.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.exerciseAdvancedToggle"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Exhaustion Level</Label>
                                <Help>
                                    Enable/Disable the exhaustion level input.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.exerciseExhaustionLevel"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Notes</Label>
                                <Help>
                                    Enable/Disable the notes related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.exerciseNotes"/>
                        </Field>

                    </FormSection>

                    <FormSection title="Meta" description="Settings related to meta data.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Previous Reps</Label>
                                <Help>
                                    Enable/Disable the Previous Reps data.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.metaPrevReps"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Max Reps</Label>
                                <Help>
                                    Enable/Disable the Max Reps data.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.metaMaxReps"/>
                        </Field>

                        <Field v-if="form.fields.metaMaxReps">
                            <Label>Max Reps Source</Label>
                            <SelectInput v-model="form.fields.metaMaxRepsSource"
                                         :options="metaMaxRepsSourceOptions"
                            />
                            <Help>
                                What should the Max Reps data point be tied to?
                            </Help>
                        </Field>

                    </FormSection>

                </FormSections>

                <Controls>

                    <template v-slot:left>

                    </template>

                    <template v-slot:right>
                        <div class="flex items-center justify-between gap-4">
                            <Button primary @click="handleSave">
                                Save
                            </Button>
                        </div>
                    </template>
                </Controls>

            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue'
import {Form} from '@/internal.js';
import {mapActions} from 'vuex';

export default defineComponent({
  name: 'Form',
  components: {},
  computed: {
    pageTitle() {
      return 'Settings';
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  created() {
    this.loadUser();
  },
  data() {
    return {
      metaMaxRepsSourceOptions: [
        {
          label: 'Max Reps for the selected Band(s)',
          value: 'selected',
        },
        {
          label: 'Max Reps for any Band(s)',
          value: 'any',
        },
      ],
      form: new Form({
        autoLastBands: false,
        autoOpenTimer: false,
        exerciseAdvancedToggle: true,
        exerciseExhaustionLevel: true,
        exerciseNotes: true,
        exerciseTimer: true,
        metaMaxReps: true,
        metaMaxRepsSource: 'selected',
        metaPrevReps: true,
        metaPrevRepsSource: 'selected',
        warnZeroReps: true,
        workoutNotes: true,
        workoutProgressPhoto: true,
        workoutTimer: true,
      }),
      loading: false,
    };
  },
  methods: {
    ...mapActions({
      myShow: 'my/show',
      mySettings: 'my/settings',
    }),
    fillForm() {
      this.form.fields = {
        ...this.form.fields,
        ...this.user.settings,
      };
    },
    handleSave() {

      if (this.loading) {
        return false;
      }

      let action = this.mySettings;
      let payload = {
        ...this.form.fields,
      };

      this.loading = true;
      action(payload).then(() => {
        window.app.snackbar({
          type: 'success',
          message: 'Settings Saved',
        });
      }).then(this.myShow).catch((error) => {
        this.form.recordErrors(error);
        window.app.snackbarError(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    loadUser() {
      this.loading = true;
      return this.myShow().then(this.fillForm)
        .catch((error) => {
          window.app.snackbarError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
})
</script>

<style scoped lang="scss">

</style>
